// Body Map: drag organs into place on a flat body, then trace what the body does with them.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { ORGANS, ORGAN_BY_ID, SYSTEM_LABEL, TIER1_IDS, PAINTED_SLOTS, PAINTED_HOTSPOTS, type Organ } from './content';
import { art, drawArt, hasArt, onArtReady } from '@kit/assets';

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

type Task =
  | { kind: 'place' }
  | { kind: 'trace'; title: string; path: string[]; why: string }          // ids incl. hotspots 'mouth','nose','windpipe','blood'
  | { kind: 'choice'; q: string; choices: string[]; answer: number; why: string }
  | { kind: 'select'; q: string; ids: string[]; why: string }               // tap all, then Done
  | { kind: 'match'; q: string; pairs: [string, string][]; why: string };   // organ id -> need

interface Hotspot { id: string; label: string; x: number; y: number }
const HOTSPOTS: Hotspot[] = [
  { id: 'mouth', label: 'Mouth', x: 0.5, y: 0.17 },
  { id: 'nose', label: 'Nose', x: 0.5, y: 0.12 },
  { id: 'windpipe', label: 'Windpipe', x: 0.5, y: 0.23 },
  { id: 'blood', label: 'Blood', x: 0.72, y: 0.6 },
];

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const ids = tier === 1 ? TIER1_IDS : ORGANS.map(o => o.id);
  const tasks: Task[] = tier === 1
    ? [{ kind: 'place' }, { kind: 'trace', title: 'Trace a bite of food', path: ['mouth', 'stomach', 'intestines'], why: 'Food goes mouth, stomach, intestines. The intestines take the good parts into your blood.' }]
    : tier === 2
      ? [{ kind: 'place' }, { kind: 'trace', title: 'Trace a breath', path: ['nose', 'windpipe', 'lungs', 'blood'], why: 'Air goes in the nose, down the windpipe, into the lungs, and oxygen passes into the blood.' }, { kind: 'choice', q: 'Which organ pumps blood around the body?', choices: ['Heart', 'Lungs', 'Liver', 'Brain'], answer: 0, why: 'The heart is a muscle pump that beats about 100,000 times a day.' }]
      : [{ kind: 'place' }, { kind: 'select', q: 'Tap every organ in the digestive system, then tap Done.', ids: ['stomach', 'intestines', 'liver'], why: 'Stomach, intestines and liver all work on food. The liver makes a juice that helps digest fat.' }, { kind: 'match', q: 'Match each part with what it needs most.', pairs: [['lungs', 'air'], ['stomach', 'food'], ['bone', 'calcium'], ['muscle', 'exercise']], why: 'Every system has a need: lungs air, stomach food, bones calcium, muscles exercise.' }, { kind: 'choice', q: 'Why do you breathe faster when you run?', choices: ['Your muscles need more oxygen', 'Your stomach is full', 'Your brain is tired', 'Your skin is cold'], answer: 0, why: 'Working muscles burn more fuel and need more oxygen, so your lungs and heart speed up.' }];
  let ti = 0;
  let attempts = 0, correct = 0;
  let firstTryAll = true;
  const t0 = performance.now();
  let tTask = performance.now();

  // placement state
  const placed = new Set<string>();
  let tray: string[] = shuffle(ctx.rng, ids);
  let drag: { id: string; x: number; y: number } | null = null;
  let wrongOn: string | null = null;
  // trace / select state
  let traceIdx = 0, traceMissed = false;
  let selected = new Set<string>();
  let selMissed = false;
  // match state
  let matchLeft: string | null = null; const matched = new Set<string>(); let matchMissed = false;

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Task ', h('strong', null, '1'), ` of ${tasks.length}`), h('span.muted.small', null, ''));
  const prompt = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const stage = h('div');
  const fact = h('p.center.muted.small', { style: { minHeight: '36px' } });
  const extra = h('div.stack', { style: { gap: '8px' } });
  host.append(head, prompt, stage, fact, extra);
  // Layout: the body on top, the tray as a grid of labeled stickers underneath (5 per row), so
  // nothing overlaps at phone width. The body box is 2:3 to match the painted base.
  const TRAY_COLS = 5, CELL_H = 78;
  const trayRows = Math.ceil(ids.length / TRAY_COLS);
  const S = makeSurface(stage, { aspect: 1.15 + trayRows * 0.2, onResize: draw });

  const painted = () => hasArt('bodymap.base');
  function slot(o: Organ) { return painted() ? { ...o, ...PAINTED_SLOTS[o.id] } : o; }
  function hotspot(hs: Hotspot) { return painted() && PAINTED_HOTSPOTS[hs.id] ? { ...hs, ...PAINTED_HOTSPOTS[hs.id] } : hs; }
  // body box in canvas coords
  function box() { const W = S.w, H = S.h; const bh = H - trayRows * CELL_H - 20, bw = Math.min(W - 16, bh / 1.5); return { x: (W - bw) / 2, y: 6, w: bw, h: bw * 1.5 }; }
  function trayBox() { const W = S.w, H = S.h; return { x: 6, y: H - trayRows * CELL_H - 8, w: W - 12, h: trayRows * CELL_H + 4 }; }
  function organPos(o: Organ) { const b = box(); const s = slot(o); return { x: b.x + s.x * b.w, y: b.y + s.y * b.h }; }
  function trayPos(i: number) { const t = trayBox(); const cw = t.w / TRAY_COLS; return { x: t.x + cw * (i % TRAY_COLS + 0.5), y: t.y + CELL_H * (Math.floor(i / TRAY_COLS) + 0.42) }; }

  function cur(): Task { return tasks[ti]; }

  function showTask() {
    const t = cur();
    (head.querySelector('strong') as HTMLElement).textContent = String(ti + 1);
    clear(extra); fact.textContent = ''; tTask = performance.now();
    traceIdx = 0; traceMissed = false; selected = new Set(); selMissed = false; matchLeft = null; matched.clear(); matchMissed = false;
    if (t.kind === 'place') prompt.textContent = `Drag each organ to where it belongs. ${ids.length} to place.`;
    else if (t.kind === 'trace') { prompt.textContent = `${t.title}: tap the parts in order. Start with ${label(t.path[0])}.`; }
    else if (t.kind === 'choice') { prompt.textContent = t.q; t.choices.forEach((c, i) => extra.append(h('button.choice', { style: { fontSize: '16px', minHeight: '52px' }, onclick: () => answerChoice(i) }, c))); }
    else if (t.kind === 'select') { prompt.textContent = t.q; extra.append(h('button.btn.primary.block', { onclick: checkSelect }, 'Done')); }
    else if (t.kind === 'match') { prompt.textContent = t.q; renderMatch(); }
    (head.lastElementChild as HTMLElement).textContent = t.kind === 'place' ? 'Drag and drop' : t.kind === 'trace' ? 'Tap in order' : '';
    ctx.speak(prompt.textContent || '');
    draw();
  }

  function label(id: string): string { return ORGAN_BY_ID[id]?.name ?? HOTSPOTS.find(hs => hs.id === id)?.label ?? id; }

  function report(ok: boolean, hinted: boolean, pid: string) {
    attempts++; if (ok) correct++;
    ctx.report({ skill: 'biology', level, correct: ok, hintUsed: hinted, ms: performance.now() - tTask, problemId: pid });
    tTask = performance.now();
  }

  function nextTask() {
    ti++;
    if (ti >= tasks.length) {
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1500);
      prompt.textContent = 'Round complete.';
      return;
    }
    setTimeout(showTask, 1600);
  }

  // ---- placement ----
  const unPointer = attachPointer(S.canvas, {
    down(p) {
      if (cur().kind !== 'place') return;
      // pick from the tray
      const cw = trayBox().w / TRAY_COLS;
      for (let i = 0; i < tray.length; i++) { const tp = trayPos(i); if (Math.abs(p.x - tp.x) < cw / 2 && Math.abs(p.y - tp.y) < CELL_H / 2) { drag = { id: tray[i], x: p.x, y: p.y }; return; } }
    },
    move(p) { if (drag) { drag.x = p.x; drag.y = p.y; draw(); } },
    up(p) {
      if (!drag) return;
      const o = ORGAN_BY_ID[drag.id];
      const tp = organPos(o);
      const b = box();
      const snap = Math.max(28, b.w * 0.12);
      if (Math.hypot(p.x - tp.x, p.y - tp.y) <= snap) {
        placed.add(o.id); tray = tray.filter(id => id !== o.id);
        const firstTry = wrongOn !== o.id;
        report(firstTry, !firstTry, `body:place:${o.id}`);
        if (!firstTry) firstTryAll = false;
        ctx.sound('ding'); fact.textContent = o.fact; ctx.say(`${o.name}: ${SYSTEM_LABEL[o.system]}.`);
        if (tray.length === 0) {
          if (ids.length === 10 && firstTryAll) ctx.charm('charm.body');
          prompt.textContent = 'All in place!';
          nextTask();
        }
      } else {
        // was it dropped on another organ's spot? say where it goes
        const near = ORGANS.filter(x => ids.includes(x.id)).find(x => { const q = organPos(x); return Math.hypot(p.x - q.x, p.y - q.y) <= snap; });
        if (p.y < trayBox().y) {
          if (wrongOn !== o.id) { wrongOn = o.id; firstTryAll = false; }
          ctx.sound('buzz');
          ctx.say(near ? `That is where the ${near.name.toLowerCase()} goes. The ${o.name.toLowerCase()} belongs ${where(o)}.` : `The ${o.name.toLowerCase()} belongs ${where(o)}.`);
        }
      }
      drag = null; draw();
    },
    tap(p) {
      const t = cur();
      if (t.kind === 'trace') tapTrace(p);
      else if (t.kind === 'select') tapSelect(p);
    },
  });

  function where(o: Organ): string {
    if (o.y < 0.15) return 'in the head';
    if (o.y < 0.4) return o.x < 0.3 ? 'in the upper arm' : o.x > 0.7 ? 'on the outside, it covers you' : 'in the chest';
    if (o.y < 0.7) return o.x > 0.7 ? 'on the outside, it covers you' : 'in the belly';
    return 'in the leg';
  }

  function hitId(p: { x: number; y: number }): string | null {
    const b = box();
    for (const h0 of HOTSPOTS) { const hs = hotspot(h0); if (Math.hypot(p.x - (b.x + hs.x * b.w), p.y - (b.y + hs.y * b.h)) < 20) return hs.id; }
    let best: string | null = null, bd = 1e9;
    for (const id of ids) { const o = ORGAN_BY_ID[id]; const q = organPos(o); const d = Math.hypot(p.x - q.x, p.y - q.y); if (d < Math.max(24, slot(o).w * b.w * 0.6) && d < bd) { bd = d; best = id; } }
    return best;
  }

  function tapTrace(p: { x: number; y: number }) {
    const t = cur(); if (t.kind !== 'trace') return;
    const id = hitId(p); if (!id) return;
    if (id === t.path[traceIdx]) {
      traceIdx++; ctx.sound('pop');
      if (traceIdx >= t.path.length) { report(!traceMissed, traceMissed, 'body:trace'); ctx.sound('ding'); ctx.say(t.why); nextTask(); }
      else ctx.say(`Yes, ${label(id)}. Now where next?`);
    } else {
      if (!traceMissed) { traceMissed = true; }
      ctx.sound('buzz'); ctx.say(`Not the ${label(id).toLowerCase()} yet. After the ${label(t.path[Math.max(0, traceIdx - 1)]).toLowerCase()} comes the ${label(t.path[traceIdx]).toLowerCase()}.`);
    }
    draw();
  }

  function tapSelect(p: { x: number; y: number }) {
    const id = hitId(p); if (!id || !ORGAN_BY_ID[id]) return;
    if (selected.has(id)) selected.delete(id); else selected.add(id);
    ctx.sound('tick'); draw();
  }
  function checkSelect() {
    const t = cur(); if (t.kind !== 'select') return;
    const want = new Set(t.ids);
    const extraIds = [...selected].filter(id => !want.has(id)), missing = t.ids.filter(id => !selected.has(id));
    if (!extraIds.length && !missing.length) { report(!selMissed, selMissed, 'body:select'); ctx.sound('ding'); ctx.say(t.why); nextTask(); }
    else {
      selMissed = true; ctx.sound('buzz');
      ctx.say(extraIds.length ? `The ${ORGAN_BY_ID[extraIds[0]].name.toLowerCase()} is part of the ${SYSTEM_LABEL[ORGAN_BY_ID[extraIds[0]].system]}, not digestion.` : `You are missing one. Which organ cleans the blood and helps digest fat?`);
    }
  }

  function renderMatch() {
    const t = cur(); if (t.kind !== 'match') return;
    clear(extra);
    const left = h('div.stack', { style: { gap: '6px', flex: '1' } }), right = h('div.stack', { style: { gap: '6px', flex: '1' } });
    const needs = shuffle(ctx.rng, t.pairs.map(p => p[1]));
    for (const [id] of t.pairs) left.append(h('button.btn.small' + (matchLeft === id ? '.primary' : '') + (matched.has(id) ? '.good' : ''), { disabled: matched.has(id), onclick: () => { matchLeft = id; renderMatch(); } }, ORGAN_BY_ID[id].name));
    for (const need of needs) {
      const doneIt = [...matched].some(id => t.pairs.find(p => p[0] === id)![1] === need);
      right.append(h('button.btn.small' + (doneIt ? '.good' : ''), { disabled: doneIt || !matchLeft, onclick: () => tryMatch(need) }, need));
    }
    extra.append(h('div.row', { style: { alignItems: 'flex-start' } }, left, right));
  }
  function tryMatch(need: string) {
    const t = cur(); if (t.kind !== 'match' || !matchLeft) return;
    const pair = t.pairs.find(p => p[0] === matchLeft)!;
    if (pair[1] === need) { matched.add(matchLeft); matchLeft = null; ctx.sound('pop'); if (matched.size === t.pairs.length) { report(!matchMissed, matchMissed, 'body:match'); ctx.sound('ding'); ctx.say(t.why); nextTask(); return; } }
    else { matchMissed = true; ctx.sound('buzz'); ctx.say(`${ORGAN_BY_ID[matchLeft].name} needs ${pair[1]} most.`); }
    renderMatch();
  }

  function answerChoice(i: number) {
    const t = cur(); if (t.kind !== 'choice') return;
    if (i === t.answer) { report(!selMissed, selMissed, 'body:choice'); ctx.sound('ding'); ctx.say(t.why); for (const b of Array.from(extra.children)) (b as HTMLButtonElement).disabled = true; nextTask(); }
    else { selMissed = true; ctx.sound('buzz'); ctx.say('Not that one. Think about which part does the job.'); }
  }

  // ---- drawing ----
  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (W < 16) return;
    c.fillStyle = '#eef3f7'; c.fillRect(0, 0, W, H);
    const b = box();
    const t = cur();
    if (painted()) drawArt(c, 'bodymap.base', b.x, b.y, b.w, b.h);
    else drawBody(c, b);
    // target outlines during placement (the painted apron already has its own dotted outlines)
    if (t.kind === 'place' && !painted()) {
      c.setLineDash([4, 4]); c.strokeStyle = 'rgba(42,42,46,.35)'; c.lineWidth = 1.5;
      for (const id of ids) if (!placed.has(id)) { const o = ORGAN_BY_ID[id]; const q = organPos(o); c.beginPath(); c.ellipse(q.x, q.y, o.w * b.w * 0.55, o.h * b.w * 0.55, 0, 0, 7); c.stroke(); }
      c.setLineDash([]);
    }
    // targets outside the apron (brain, muscle, skin, bone) get a soft ring on the painted body
    if (t.kind === 'place' && painted()) {
      c.setLineDash([4, 4]); c.strokeStyle = 'rgba(59,42,30,.45)'; c.lineWidth = 1.5;
      for (const id of ids) if (!placed.has(id) && ['brain', 'muscle', 'skin', 'bone'].includes(id)) { const s = slot(ORGAN_BY_ID[id]); const q = organPos(ORGAN_BY_ID[id]); c.beginPath(); c.ellipse(q.x, q.y, s.w * b.w * 0.55, s.h * b.w * 0.55, 0, 0, 7); c.stroke(); }
      c.setLineDash([]);
    }
    // hotspots for trace tasks
    if (t.kind === 'trace') {
      for (const h0 of HOTSPOTS) if (t.path.includes(h0.id)) { const hs = hotspot(h0); const x = b.x + hs.x * b.w, y = b.y + hs.y * b.h; const idx = t.path.indexOf(hs.id); c.fillStyle = idx < traceIdx ? '#5fbf6a' : 'rgba(79,143,230,.75)'; c.beginPath(); c.arc(x, y, 9, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.font = '800 11px Nunito, sans-serif'; c.textAlign = 'left'; pill(c, hs.label, x + 12, y); }
    }
    // placed organs
    for (const id of ids) if (placed.has(id)) { const o = ORGAN_BY_ID[id]; const s = slot(o); const q = organPos(o); drawOrgan(c, o, q.x, q.y, s.w * b.w, s.h * b.w, (t.kind === 'select' && selected.has(id)) || (t.kind === 'trace' && t.path.indexOf(id) >= 0 && t.path.indexOf(id) < traceIdx)); }
    // tray: a grid of stickers, label under each
    if (t.kind === 'place') {
      const tb = trayBox(); const cw = tb.w / TRAY_COLS;
      c.fillStyle = '#fff'; c.beginPath(); c.roundRect(tb.x, tb.y, tb.w, tb.h, 10); c.fill();
      tray.forEach((id, i) => {
        if (drag && drag.id === id) return;
        const o = ORGAN_BY_ID[id]; const tp = trayPos(i); const s = slot(o);
        const k = Math.min((cw - 10) / Math.max(s.w * b.w, 1), 44 / Math.max(s.h * b.w, 1), 1.6);
        drawOrgan(c, o, tp.x, tp.y, s.w * b.w * k, s.h * b.w * k, false);
        c.fillStyle = '#2a2a2e'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
        c.fillText(o.name.replace(/ \(.*\)/, ''), tp.x, tp.y + CELL_H * 0.5);
      });
      if (drag) { const o = ORGAN_BY_ID[drag.id]; const s = slot(o); drawOrgan(c, o, drag.x, drag.y, s.w * b.w, s.h * b.w, true); }
    }
  }

  function pill(c: CanvasRenderingContext2D, text: string, x: number, y: number) {
    const w = c.measureText(text).width + 10;
    c.fillStyle = 'rgba(255,255,255,.9)'; c.beginPath(); c.roundRect(x, y - 9, w, 18, 9); c.fill();
    c.fillStyle = '#2a2a2e'; c.textBaseline = 'middle'; c.fillText(text, x + 5, y + 1); c.textBaseline = 'alphabetic';
  }

  function drawBody(c: CanvasRenderingContext2D, b: { x: number; y: number; w: number; h: number }) {
    // body outline
    c.fillStyle = '#f3dcc8';
    const hx = b.x + b.w / 2, hy = b.y + b.h * 0.09, hr = b.w * 0.14;
    c.beginPath(); c.arc(hx, hy, hr, 0, 7); c.fill();
    c.fillRect(hx - b.w * 0.05, hy + hr - 2, b.w * 0.1, b.h * 0.05);            // neck
    c.beginPath(); c.roundRect(b.x + b.w * 0.27, b.y + b.h * 0.2, b.w * 0.46, b.h * 0.5, 14); c.fill();   // torso
    c.beginPath(); c.roundRect(b.x + b.w * 0.13, b.y + b.h * 0.22, b.w * 0.13, b.h * 0.42, 10); c.fill();  // left arm
    c.beginPath(); c.roundRect(b.x + b.w * 0.74, b.y + b.h * 0.22, b.w * 0.13, b.h * 0.42, 10); c.fill();  // right arm
    c.beginPath(); c.roundRect(b.x + b.w * 0.3, b.y + b.h * 0.68, b.w * 0.18, b.h * 0.3, 10); c.fill();    // left leg
    c.beginPath(); c.roundRect(b.x + b.w * 0.52, b.y + b.h * 0.68, b.w * 0.18, b.h * 0.3, 10); c.fill();   // right leg
    // face
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(hx - hr * 0.35, hy - hr * 0.1, 2.5, 0, 7); c.arc(hx + hr * 0.35, hy - hr * 0.1, 2.5, 0, 7); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 2; c.beginPath(); c.arc(hx, hy + hr * 0.2, hr * 0.4, 0.2, Math.PI - 0.2); c.stroke();
  }

  function drawOrgan(c: CanvasRenderingContext2D, o: Organ, x: number, y: number, w: number, hh: number, hi: boolean) {
    const img = art(`bodymap.organ.${o.id}`);
    if (img) {
      // stickers are square with a margin; scale so the organ fills its slot, centered
      const side = Math.max(w, hh) * 1.35;
      c.drawImage(img, x - side / 2, y - side / 2, side, side);
      if (hi) { c.strokeStyle = '#3bb3a9'; c.lineWidth = 3; c.beginPath(); c.ellipse(x, y, w / 2 + 6, hh / 2 + 6, 0, 0, 7); c.stroke(); }
      return;
    }
    c.save(); c.translate(x, y);
    c.fillStyle = o.color;
    switch (o.shape) {
      case 'brain': c.beginPath(); c.ellipse(0, 0, w / 2, hh / 2, 0, 0, 7); c.fill(); c.strokeStyle = 'rgba(0,0,0,.25)'; c.lineWidth = 2; c.beginPath(); c.moveTo(0, -hh / 2); c.lineTo(0, hh / 2); c.moveTo(-w / 4, -hh / 4); c.quadraticCurveTo(-w / 8, 0, -w / 4, hh / 4); c.moveTo(w / 4, -hh / 4); c.quadraticCurveTo(w / 8, 0, w / 4, hh / 4); c.stroke(); break;
      case 'lungs': c.beginPath(); c.ellipse(-w * 0.27, 0, w * 0.22, hh / 2, 0, 0, 7); c.fill(); c.beginPath(); c.ellipse(w * 0.27, 0, w * 0.22, hh / 2, 0, 0, 7); c.fill(); c.fillStyle = '#e9b995'; c.fillRect(-3, -hh / 2 - 6, 6, hh / 2); break;
      case 'heart': c.beginPath(); c.moveTo(0, hh / 2); c.bezierCurveTo(-w, -hh / 4, -w / 4, -hh, 0, -hh / 4); c.bezierCurveTo(w / 4, -hh, w, -hh / 4, 0, hh / 2); c.fill(); break;
      case 'stomach': c.beginPath(); c.ellipse(0, 0, w / 2, hh / 2, -0.4, 0, 7); c.fill(); break;
      case 'intestines': c.strokeStyle = o.color; c.lineWidth = Math.max(6, hh * 0.3); c.lineCap = 'round'; c.beginPath(); for (let i = 0; i < 4; i++) { const yy = -hh / 2 + (i + 0.5) * (hh / 4); c.moveTo(-w / 2 + 4, yy); c.lineTo(w / 2 - 4, yy); if (i < 3) c.arc(i % 2 ? -w / 2 + 4 : w / 2 - 4, yy + hh / 8, hh / 8, i % 2 ? Math.PI / 2 : -Math.PI / 2, i % 2 ? Math.PI * 1.5 : Math.PI / 2, i % 2 === 0); } c.stroke(); break;
      case 'liver': c.beginPath(); c.moveTo(-w / 2, -hh / 2); c.lineTo(w / 2, -hh / 3); c.lineTo(w / 4, hh / 2); c.lineTo(-w / 2, hh / 3); c.closePath(); c.fill(); break;
      case 'kidneys': for (const dx of [-w * 0.3, w * 0.3]) { c.beginPath(); c.ellipse(dx, 0, w * 0.12, hh / 2 + 4, 0, 0, 7); c.fill(); } break;
      case 'bone': c.fillStyle = o.color; c.strokeStyle = '#9a9aa3'; c.lineWidth = 1.5; c.beginPath(); c.roundRect(-w / 2, -hh / 2, w, hh, w / 2); c.fill(); c.stroke(); c.beginPath(); c.arc(-w / 2, -hh / 2, w * 0.7, 0, 7); c.arc(w / 2, -hh / 2, w * 0.7, 0, 7); c.arc(-w / 2, hh / 2, w * 0.7, 0, 7); c.arc(w / 2, hh / 2, w * 0.7, 0, 7); c.fill(); break;
      case 'muscle': c.beginPath(); c.ellipse(0, 0, w / 2 + 3, hh / 2, 0, 0, 7); c.fill(); c.strokeStyle = 'rgba(0,0,0,.25)'; c.lineWidth = 2; c.beginPath(); c.moveTo(0, -hh / 2 + 4); c.lineTo(0, hh / 2 - 4); c.stroke(); break;
      case 'skin': c.beginPath(); c.roundRect(-w / 2, -hh / 2, w, hh, 4); c.fill(); c.fillStyle = 'rgba(0,0,0,.12)'; for (let i = 0; i < 4; i++) c.fillRect(-w / 2 + 4 + i * (w / 4), -hh / 2 + 4, 2, hh - 8); break;
    }
    if (hi) { c.strokeStyle = '#3bb3a9'; c.lineWidth = 3; c.beginPath(); c.ellipse(0, 0, w / 2 + 6, hh / 2 + 6, 0, 0, 7); c.stroke(); }
    c.restore();
  }

  showTask();
  const unArt = onArtReady(draw);
  return { destroy() { unArt(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.body-map',
  title: 'Body Map',
  blurb: 'Put the organs where they go, then trace a bite and a breath.',
  category: 'science',
  skills: ['biology'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
